import React from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationProps {
  // Mode: 'dynamic' for server-side, 'static' for client-side
  mode?: "dynamic" | "static";

  // For dynamic mode (server-side pagination)
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;

  // For static mode (client-side pagination)
  // If mode is static, these will be calculated from dataLength
  dataLength?: number;

  // Callbacks
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  // Options
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  mode = "static",
  currentPage = 1,
  totalPages,
  totalItems,
  pageSize = 10,
  hasPrevious,
  hasNext,
  dataLength,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 25, 50, 100],
  showPageSizeSelector = true,
  className = "",
}) => {
  // Calculate values based on mode
  const effectiveTotalItems =
    mode === "dynamic" ? (totalItems ?? 0) : (dataLength ?? 0);

  const effectiveTotalPages =
    mode === "dynamic"
      ? (totalPages ?? 1)
      : Math.ceil((dataLength ?? 0) / pageSize);

  const effectiveCurrentPage = currentPage;

  const startItem =
    effectiveTotalItems > 0 ? (effectiveCurrentPage - 1) * pageSize + 1 : 0;

  const endItem = Math.min(
    effectiveCurrentPage * pageSize,
    effectiveTotalItems
  );

  const canGoPrevious =
    mode === "dynamic" ? (hasPrevious ?? false) : effectiveCurrentPage > 1;

  const canGoNext =
    mode === "dynamic"
      ? (hasNext ?? false)
      : effectiveCurrentPage < effectiveTotalPages;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (effectiveTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= effectiveTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (effectiveCurrentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(effectiveTotalPages);
      } else if (effectiveCurrentPage >= effectiveTotalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = effectiveTotalPages - 3; i <= effectiveTotalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (
          let i = effectiveCurrentPage - 1;
          i <= effectiveCurrentPage + 1;
          i++
        ) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(effectiveTotalPages);
      }
    }

    return pages;
  };

  if (effectiveTotalItems === 0) return null;

  return (
    <div
      className={`from-gray-50 to-white border border-gray-100 rounded-lg ${className}`}
    >
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-2">
        {/* Rows per page selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
              Show
            </span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={e => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1); // Reset to first page when page size changes
                }}
                className="appearance-none px-2.5 py-1 pr-7 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer"
              >
                {pageSizeOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none">
                <svg
                  className="w-3.5 h-3.5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
              entries
            </span>
          </div>
        )}

        {/* Page info */}
        <div className="flex items-center">
          <span className="text-xs font-medium text-gray-600">
            Showing{" "}
            <span className="font-semibold text-gray-900">{startItem}</span> to{" "}
            <span className="font-semibold text-gray-900">{endItem}</span> of{" "}
            <span className="font-semibold text-gray-900">
              {effectiveTotalItems}
            </span>{" "}
            results
          </span>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(Math.max(1, effectiveCurrentPage - 1))}
            disabled={!canGoPrevious}
            className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300 disabled:hover:text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-0.5">
            {getPageNumbers().map((page, index) => {
              if (page === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-1.5 py-0.5 text-xs text-gray-400 font-medium"
                  >
                    ...
                  </span>
                );
              }

              const isActive = effectiveCurrentPage === page;
              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page as number)}
                  className={`min-w-[28px] h-7 px-2 text-xs rounded-md border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 
                    ${
                      isActive
                        ? "bg-gradient-to-br from-purple-600 to-purple-700 text-white border-purple-600 shadow-md shadow-purple-200 hover:from-purple-700 hover:to-purple-800"
                        : "bg-white text-gray-700 border-gray-300 shadow-sm hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700"
                    }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              onPageChange(
                Math.min(effectiveTotalPages, effectiveCurrentPage + 1)
              )
            }
            disabled={!canGoNext}
            className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300 disabled:hover:text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
